interface NotificationSettings {
  newMessageAlerts: boolean;
  emailNotifications: boolean;
  desktopNotifications: boolean;
  soundEnabled: boolean;
}

class NotificationService {
  private settings: NotificationSettings;

  constructor() {
    this.settings = this.loadSettings();
  }

  private loadSettings(): NotificationSettings {
    const saved = localStorage.getItem('notificationSettings');
    if (saved) {
      return JSON.parse(saved);
    }
    return {
      newMessageAlerts: true,
      emailNotifications: false,
      desktopNotifications: false,
      soundEnabled: true
    };
  }

  private saveSettings() {
    localStorage.setItem('notificationSettings', JSON.stringify(this.settings));
  }

  private playNotificationSound() {
    if (!this.settings.soundEnabled) return;

    try {
      // Create a pleasant notification sound
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Pleasant notification tone
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  }

  private showDesktopNotification(title: string, body: string, icon?: string) {
    if (!this.settings.desktopNotifications) return;
    
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        icon: icon || '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'whatsapp-message', // This replaces previous notifications
        requireInteraction: false
      });

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      // Click handler to focus the window
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  }

  // Main method to show new message notification
  public notifyNewMessage(contactName: string, message: string, chatId?: string) {
    console.log('NotificationService.notifyNewMessage called:', { contactName, message, chatId });
    
    // Refresh settings in case they changed
    this.settings = this.loadSettings();
    console.log('Notification settings:', this.settings);
    
    if (!this.settings.newMessageAlerts) {
      console.log('New message alerts disabled, skipping notification');
      return;
    }

    const title = `New WhatsApp message`;
    const body = `${contactName}: ${message.length > 80 ? message.substring(0, 80) + '...' : message}`;

    // Play sound notification
    this.playNotificationSound();

    // Show desktop notification
    this.showDesktopNotification(title, body);

    // Update document title if page is not focused
    if (document.hidden) {
      const originalTitle = document.title;
      document.title = `(1) New message from ${contactName}`;
      
      // Reset title when page becomes visible
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          document.title = originalTitle;
          document.removeEventListener('visibilitychange', handleVisibilityChange);
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }
  }

  // Method to request desktop notification permission
  public async requestPermission(): Promise<NotificationPermission> {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        // Show a test notification
        this.showDesktopNotification(
          'WhatsApp Chat',
          'Desktop notifications enabled!'
        );
      }
      return permission;
    }
    return 'denied';
  }

  // Test notification
  public testNotification() {
    this.showDesktopNotification(
      'Test Notification',
      'This is a test notification from WhatsApp Chat'
    );
    this.playNotificationSound();
  }

  // Update a specific setting
  public updateSetting(key: keyof NotificationSettings, value: boolean) {
    this.settings = { ...this.settings, [key]: value };
    this.saveSettings();
  }

  // Get current settings
  public getSettings(): NotificationSettings {
    return { ...this.settings };
  }

  // Get current permission status
  public getPermissionStatus(): NotificationPermission {
    if ('Notification' in window) {
      return Notification.permission;
    }
    return 'denied';
  }
}

export const notificationService = new NotificationService();
